/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/naming-convention */
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Inject, Injectable, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DialogsService } from '../dialogs.service';
import { LocaleString } from '../../core/translate/localeString.model';
import { LocalizePipe } from '../../core/pipes/localize.pipe';
import { MasterDataService } from '../../core/master-data/master-data.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';

/**
 * Node for to-do item
 */
export class CategoryNode {
    category?: CategoryNode[];
    name: LocaleString;
    oid: string;
}

/** Flat to-do item node with expandable and level information */
export class CategoryFlatNode {
    item: CategoryNode;
    level: number;
    expandable: boolean;
    addingChild?: boolean = false;
    hasChild: boolean;
    addCategoryNodeControl: FormControl = new FormControl();
    errorAddCategoryNode: boolean = false;
}

export class CategoryToReturn {
    name: string;
    oid: string;
}

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */

@Injectable({ providedIn: 'root' })
export class OidGenerator {

    constructor(private masterDataService: MasterDataService) { }

    async getOid() {
        let oids: string[] = JSON.parse(localStorage.getItem('oid-values'));
        if (!oids?.length) {
            await this.nextValue();
            oids = JSON.parse(localStorage.getItem('oid-values'));
        }
        const value = oids[0];
        oids = oids.slice(1, oids.length);
        localStorage.setItem('oid-values', JSON.stringify(oids));
        return value;
    }

    async nextValue() {
        const body = {
            sequenceName: 'MASTER_OID_SEQUENCE',
            chunkSize: 20
        };

        const value = await firstValueFrom(this.masterDataService.nextValue(body));
        localStorage.setItem('oid-values', JSON.stringify(value.value));
    }
}
@Injectable()
export class ChecklistDatabaseCategories {
    dataChange = new BehaviorSubject<CategoryNode[]>([]);

    constructor(@Inject(MAT_DIALOG_DATA) public dataTree: {
        uri: string;
        tree: CategoryNode[];
    },
        private localize: LocalizePipe,
        private transloco: TranslocoService,
        private oidGenerator: OidGenerator) {
        this.initialize();
    }

    get data(): CategoryNode[] {
        return this.dataChange.value;
    }

    initialize() {
        // Build the tree nodes from Json object. The result is a list of `TodoItemNode` with nested
        //     file node as children.
        const data = this.dataTree.tree ?? [];
        // Notify the change.
        this.dataChange.next(data);
    }

    /**
     * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
     * The return value is the list of `TodoItemNode`.
     */
    buildFileTree(tree: CategoryNode[], level: number): CategoryNode[] {
        return tree.reduce<CategoryNode[]>((accumulator, key) => {
            const node = new CategoryNode();
            for (const value of tree) {
                node.name = value.name;
                node.oid = value.oid;

                if (value.category) {
                    node.category = this.buildFileTree(value.category, level + 1);
                }
                accumulator.concat(node);
            }
            return accumulator.concat(node);
        }, []);
    }

    /** Add an item to to-do list */
    async insertItem(parent: CategoryNode, name: string) {
        if (!parent.category) {
            parent.category = [];
        }
        const language = this.transloco.getActiveLang();
        const oid = await this.oidGenerator.getOid();
        parent.category.push({ name: { [language]: name }, oid: oid } as CategoryNode);
        this.dataChange.next(this.data);
    }

    async createItem(name: string) {
        const language = this.transloco.getActiveLang();
        const oid = await this.oidGenerator.getOid();
        this.data.push({ name: { [language]: name }, oid: oid } as CategoryNode);
        this.dataChange.next(this.data);
    }

    updateItem(node: CategoryNode, name: string = undefined, categoryOid: string = undefined) {
        const language = this.transloco.getActiveLang();
        if (name) {
            node.name = { [language]: name };
        }

        if (categoryOid) {
            node.oid = categoryOid;
        }

        this.dataChange.next(this.data);
    }

    removeItem(parentNode: CategoryNode, categoryOid: string) {
        if (parentNode) {
            parentNode.category = parentNode.category.filter(c => c.oid !== categoryOid);
            this.dataChange.next(this.data);
        } else {
            this.dataChange.next(this.data.filter(c => c.oid !== categoryOid));
        }
    }

}

/**
 * @title Tree with checkboxes
 */
@Component({
    selector: 'app-category-tree-dialog',
    templateUrl: 'category-tree-dialog.component.html',
    providers: [ChecklistDatabaseCategories, OidGenerator],
    styles: [
        `.pad {
            width: 40px;
        }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

/** COMPONENT */
export class CategoryTreeDialogComponent implements OnInit {
    /** Map from flat node to nested node. This helps us finding the nested node to be modified */
    flatNodeMap = new Map<CategoryFlatNode, CategoryNode>();

    /** Map from nested node to flattened node. This helps us to keep the same object for selection */
    nestedNodeMap = new Map<CategoryNode, CategoryFlatNode>();

    /** A selected parent node to be inserted */
    selectedParent: CategoryFlatNode | null = null;

    /** The new item's name */
    newItemName = '';

    treeControl: FlatTreeControl<CategoryFlatNode>;

    treeFlattener: MatTreeFlattener<CategoryNode, CategoryFlatNode>;

    dataSource: MatTreeFlatDataSource<CategoryNode, CategoryFlatNode>;

    /** The selection for checklist */
    checklistSelection = new SelectionModel<CategoryFlatNode>(true /* multiple */);

    hasChanges = false;
    addingNode = false;
    viewEdit = false;
    addRootCategoryNodeControl: FormControl = new FormControl();
    errorAddRootCategoryNode: boolean = false;
    categoriesToReturn: CategoryToReturn[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA) public dataDialog: {
            uri: string;
            tree: CategoryNode[];
            masterName: string;
            listOids: string[];
        },
        private _database: ChecklistDatabaseCategories,
        private localize: LocalizePipe,
        private oidGenerator: OidGenerator,
        private masterDataService: MasterDataService,
        public dialogRef: MatDialogRef<CategoryTreeDialogComponent>,
        private transloco: TranslocoService,
        private _fuseConfirmationService: FuseConfirmationService,
        private dialogsService: DialogsService) {
        this.treeFlattener = new MatTreeFlattener(
            this.transformer,
            this.getLevel,
            this.isExpandable,
            this.getChildren,
        );
        this.treeControl = new FlatTreeControl<CategoryFlatNode>(this.getLevel, this.isExpandable);
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

        _database.dataChange.subscribe((data) => {
            this.dataSource.data = data;
        });
    }

    ngOnInit() {
        if (this.dataDialog.listOids.length > 0) {
            for (const o of this.dataDialog.listOids) {
                const node = this.treeControl.dataNodes.find(n => n.item.oid === o);
                if (node) {
                    this.toggleNode(node);
                    this.expandChildNode(this._database.data, o);
                }
            }
        }

        if (this.dataSource.data.length === 0) {
            this.viewEdit = true;
        }
    }

    getLevel = (node: CategoryFlatNode) => node.level;

    isExpandable = (node: CategoryFlatNode) => node.expandable;

    getChildren = (node: CategoryNode): CategoryNode[] => node.category;

    hasChild = (_: number, _nodeData: CategoryFlatNode) => _nodeData.expandable;

    hasNoContent = () => this._database.data.length === 0;

    /**
     * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
     */
    transformer = (node: CategoryNode, level: number) => {
        const existingNode = this.nestedNodeMap.get(node);
        const flatNode =
            existingNode && existingNode.item === node ? existingNode : new CategoryFlatNode();
        flatNode.item = node;
        flatNode.level = level;
        flatNode.expandable = true;
        flatNode.hasChild = !!node.category?.length;
        this.flatNodeMap.set(flatNode, node);
        this.nestedNodeMap.set(node, flatNode);
        return flatNode;
    };

    /** Toggle a node selection*/
    toggleNode(node: CategoryFlatNode) {
        this.checklistSelection.toggle(node);
    }

    /** Returns if a node is checked*/
    isChecked(node: CategoryFlatNode) {
        return this.checklistSelection.isSelected(node);
    }


    /* Get the parent node of a node */
    getParentNode(node: CategoryFlatNode): CategoryFlatNode | null {
        const currentLevel = this.getLevel(node);

        if (currentLevel < 1) {
            return null;
        }

        const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

        for (let i = startIndex; i >= 0; i--) {
            const currentNode = this.treeControl.dataNodes[i];

            if (this.getLevel(currentNode) < currentLevel) {
                return currentNode;
            }
        }
        return null;
    }

    /** Select the category so we can insert the new item. */
    addNewItem(node: CategoryFlatNode, value: string) {
        if (value) {
            node.errorAddCategoryNode = false;
            node.addCategoryNodeControl.setErrors({ 'incorrect': false });
            node.addingChild = false;
            node.expandable = true;
            const parentNode = this.flatNodeMap.get(node);
            this._database.insertItem(parentNode!, value);
            node.addCategoryNodeControl.setValue(undefined);
            this.treeControl.expand(node);
        } else {
            node.addCategoryNodeControl.markAsTouched();
            node.addCategoryNodeControl.updateValueAndValidity();
            node.errorAddCategoryNode = true;
            node.addCategoryNodeControl.setErrors({ 'incorrect': true });
        }
    }

    /** Save the node to database */
    saveNode(node: CategoryFlatNode, itemValue: string) {
        const nestedNode = this.flatNodeMap.get(node);
        this._database.updateItem(nestedNode!, itemValue);
    }

    /** Create new main node */
    createMainNode(itemValue: string) {
        if (itemValue) {
            this.errorAddRootCategoryNode = false;
            this.addRootCategoryNodeControl.setErrors({ 'incorrect': false });
            this._database.createItem(itemValue);
            this.addRootCategoryNodeControl.setValue(undefined);
        } else {
            this.addRootCategoryNodeControl.markAsTouched();
            this.addRootCategoryNodeControl.updateValueAndValidity();
            this.errorAddRootCategoryNode = true;
            this.addRootCategoryNodeControl.setErrors({ 'incorrect': true });
        }
    }

    /** Expand all parent nodes until selected child node */
    expandChildNode(data: CategoryNode[], oid: string): any {
        data.forEach((node) => {
            if (node.category && node.category.find(c => c.oid === oid)) {
                this.treeControl.expand(this.nestedNodeMap.get(node));
                this.expandChildNode(this._database.data, node.oid);
            }
            else if (node.category && node.category.find(c => node.category)) {
                this.expandChildNode(node.category, oid);
            }
        });
    }

    /** Cancel adding child */
    cancelAddingChild(node: CategoryFlatNode){
        node.addingChild = false;
        node.errorAddCategoryNode = false;
    }

    /** Remove the node from database */
    async removeNode(node: CategoryFlatNode) {
        const confirm = await this.confirm(this.transloco.translate('warning'), this.transloco.translate('removeCategoryWarn', { name: this.localize.transform(node.item.name) }));
        let parentFlat;
        if (confirm) {
            if(this.isChecked(node)){
                this.checklistSelection.toggle(node);
            }
            if (this._database.data.find(d => d.oid === node.item.oid)) {
                parentFlat = undefined;
            } else {
                const parentNode = this.getParentNode(node);
                parentFlat = this.flatNodeMap.get(parentNode);
            }
            this._database.removeItem(parentFlat!, node.item.oid);
        }
    }

    /** Flush tree to server */
    async flushTree() {
        const confirm = await this.confirm(this.transloco.translate('warning'), this.transloco.translate('saveChangesAndCategoriesMessage'));
        if (confirm) {
            const reply = await firstValueFrom(this.masterDataService.flushMasterCategoryTree(this.dataDialog.uri, this._database.data));
            if (reply.status === 200) {
                for (const category of this.checklistSelection.selected) {
                    const c: CategoryToReturn = { name: this.localize.transform(category.item.name), oid: category.item.oid };
                    this.categoriesToReturn.push(c);
                }
                this.closeDialog(this.categoriesToReturn);
            } else {
                this.dialogsService.openErrorDialog({
                    message: this.transloco.translate('services.resources.errorDialogMsg')
                });
                this.closeDialog();
            }
        }
    }

    async closeDialog(result?: any) {
        if (this.hasChanges) {
            const confirm = await this.confirm(null, null, this.transloco.translate('exitWithoutSaving'));
            if (confirm) {
                this.dialogRef.close(result);
            }
        } else {
            this.dialogRef.close(result);
        }
    }

    async removeTree(){
        const confirm = await this.confirm(this.transloco.translate('warning'), this.transloco.translate('removeTree'));
        if (confirm) {
            this._database.dataChange.next([]);
        }
    }

    private confirm(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.transloco.translate('unsavedChanges'),
            message: message ? message : this.transloco.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }
}
